import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import useFinanceDesignContext from '../hooks/useFinanceDesignContext';


interface PayPalPayment {
  id: number;
  member_name: string;
  amount: number;
  payment_date: string;
  cashbox_date?: string;
  accounted: number;
  event_label: string;
  abrechnungsmonat?: string;
}

interface Account {
  id: number;
  name: string;
  kontotyp: string;
}

// Avatar helpers
const avatarColors = [
  { bg: 'bg-primary-100 dark:bg-primary-900/30', text: 'text-primary-700 dark:text-primary-300' },
  { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
  { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-300' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
  { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300' },
  { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
  { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300' },
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const getInitials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const PayPalPayments: React.FC = () => {
  useFinanceDesignContext();
  const { user } = useContext(AuthContext);
  const [groupedPayments, setGroupedPayments] = useState<Record<string, PayPalPayment[]>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [selectedPayments, setSelectedPayments] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<number, boolean>>({});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  const chartScrollRef = useRef<HTMLDivElement>(null);

  // ConfirmationModal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  // Verfügbare Jahre abrufen
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await apiClient.get('/api/drinks/paypal-payments/years');
        setAvailableYears(res.data);
      } catch (err) {
        console.error('Fehler beim Abrufen der Jahre:', err);
      }
    };
    fetchYears();
  }, []);

  // Zahlungen und Konten abrufen
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams();
        if (showAll) params.append('all', 'true');
        if (selectedYear) params.append('year', selectedYear.toString());

        const paymentsRes = await apiClient.get(`/api/drinks/paypal-payments?${params.toString()}`);
        const paymentsData = paymentsRes.data;

        const accountsRes = await apiClient.get('/api/finance/accounts');
        setAccounts(accountsRes.data);

        const grouped = paymentsData.reduce((acc: Record<string, PayPalPayment[]>, payment: PayPalPayment) => {
          const event = payment.event_label || 'Unbekanntes Event';
          if (!acc[event]) acc[event] = [];
          acc[event].push(payment);
          return acc;
        }, {});

        setGroupedPayments(grouped);
        setExpandedGroups(Object.keys(grouped).reduce((acc, event) => ({ ...acc, [event]: true }), {}));
      } catch (err: any) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Fehler beim Laden der PayPal-Zahlungen',
          variant: 'single',
          onConfirm: () => {},
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showAll, selectedYear]);

  // Zahlung(en) als verbucht markieren
  const handleMarkAsAccounted = async (paymentIds: number[]) => {
    setProcessing(paymentIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}));

    try {
      const requests = paymentIds.map((id) =>
        apiClient.put(`/api/drinks/paypal-payments/${id}/accounted`).then(() => id)
      );

      const updatedIds = await Promise.all(requests);

      const totalAmount = Object.values(groupedPayments)
        .flat()
        .filter((payment) => updatedIds.includes(payment.id))
        .reduce((sum, payment) => sum + payment.amount, 0)
        .toFixed(2);

      const kantineAccount = accounts.find((acc) => acc.name === 'Kantine');
      if (!kantineAccount) throw new Error('Kantinenkonto nicht gefunden');

      const eventSet: { [key: string]: boolean } = {};
      Object.values(groupedPayments)
        .flat()
        .filter((payment) => updatedIds.includes(payment.id))
        .forEach((payment) => { eventSet[payment.event_label] = true; });
      const events = Object.keys(eventSet).join(', ');
      const description = `Einzahlung von ${updatedIds.length} PayPal-Zahlungen (${totalAmount} EUR) für Events: ${events}`;

      const formData = new FormData();
      formData.append('type', 'deposit');
      formData.append('amount', totalAmount);
      formData.append('date', new Date().toISOString().split('T')[0]);
      formData.append('category_id', '5');
      formData.append('account_id', kantineAccount.id.toString());
      formData.append('description', description);

      await apiClient.post('/api/finance/bookings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setGroupedPayments((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((event) => {
          updated[event] = updated[event].map((payment) =>
            updatedIds.includes(payment.id)
              ? { ...payment, accounted: 1, cashbox_date: new Date().toISOString() }
              : payment
          );
        });
        return updated;
      });

      setSelectedPayments((prev) => prev.filter((id) => !updatedIds.includes(id)));

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: `${updatedIds.length} Zahlung(en) erfolgreich verbucht und ${totalAmount} EUR in die Kantine eingezahlt`,
        variant: 'single',
        onConfirm: () => {},
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.message || 'Fehler beim Verbuchen der Zahlungen',
        variant: 'single',
        onConfirm: () => {},
      });
    } finally {
      setProcessing({});
    }
  };

  const toggleGroup = (event: string) => {
    setExpandedGroups((prev) => ({ ...prev, [event]: !prev[event] }));
  };

  const toggleSelectPayment = (paymentId: number) => {
    setSelectedPayments((prev) =>
      prev.includes(paymentId) ? prev.filter((id) => id !== paymentId) : [...prev, paymentId]
    );
  };

  const toggleSelectAllInGroup = (event: string) => {
    const groupPayments = groupedPayments[event].filter((p) => !p.accounted).map((p) => p.id);
    const allSelected = groupPayments.every((id) => selectedPayments.includes(id));
    if (allSelected) {
      setSelectedPayments((prev) => prev.filter((id) => !groupPayments.includes(id)));
    } else {
      setSelectedPayments((prev) => {
        const combined = [...prev, ...groupPayments];
        return combined.filter((id, index) => combined.indexOf(id) === index);
      });
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateCompact = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  // Statistiken
  const allPayments = useMemo(() => Object.values(groupedPayments).flat(), [groupedPayments]);
  const stats = useMemo(() => ({
    total: allPayments.length,
    verbucht: allPayments.filter(p => p.accounted).length,
    offen: allPayments.filter(p => !p.accounted).length,
    summe: allPayments.reduce((sum, p) => sum + p.amount, 0),
    summeVerbucht: allPayments.filter(p => p.accounted).reduce((sum, p) => sum + p.amount, 0),
    summeOffen: allPayments.filter(p => !p.accounted).reduce((sum, p) => sum + p.amount, 0),
  }), [allPayments]);

  // Monatliche Aggregation für Balkendiagramm (nach abrechnungsmonat, verbucht/offen)
  const monthlyData = useMemo(() => {
    const months: Record<string, { verbucht: number; offen: number }> = {};
    allPayments.forEach(p => {
      // abrechnungsmonat aus drinks_events nutzen, Fallback auf payment_date
      const dateStr = p.abrechnungsmonat || p.payment_date;
      const d = new Date(dateStr);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!months[key]) months[key] = { verbucht: 0, offen: 0 };
      if (p.accounted) {
        months[key].verbucht += p.amount;
      } else {
        months[key].offen += p.amount;
      }
    });
    const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
    const max = Math.max(...sorted.map(([, v]) => v.verbucht + v.offen), 1);
    return sorted.map(([key, { verbucht, offen }]) => ({
      label: new Date(key + '-01').toLocaleDateString('de-DE', { month: 'short' }),
      verbucht,
      offen,
      total: verbucht + offen,
      verbuchtPercent: (verbucht / max) * 100,
      offenPercent: (offen / max) * 100,
    }));
  }, [allPayments]);

  const totalAmount = useMemo(() =>
    allPayments.filter(p => selectedPayments.includes(p.id)).reduce((sum, p) => sum + p.amount, 0),
    [allPayments, selectedPayments]
  );

  // Chart nach rechts scrollen (neueste Monate sichtbar)
  useEffect(() => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
    }
  }, [monthlyData]);

  // Donut-Werte
  const verbuchtPercent = stats.total > 0 ? (stats.verbucht / stats.total) * 100 : 0;

  if (loading) {
    return (
      <div className="finance-page max-w-7xl mx-auto">
        {/* Skeleton Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2">
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
          <div className="h-0.5 bg-gray-200 dark:bg-gray-700 animate-pulse" />
        </div>
        {/* Skeleton Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 mb-6">
          <div className="lg:col-span-3 h-40 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          <div className="lg:col-span-5 h-40 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          <div className="hidden lg:block lg:col-span-4 h-40 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        </div>
        {/* Skeleton Groups */}
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-xl h-16 mb-3 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="finance-page max-w-7xl mx-auto">
      {/* Header */}
      <header className="app-page-header finance-page-header flex items-center justify-between p-5 sm:p-6 mb-6">
        <div className="flex items-center gap-4">
          <span className="finance-header-icon">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 01-.633-.74L4.944 2.998A1.564 1.564 0 016.48 1.67h6.317c2.094 0 3.58.562 4.418 1.67.364.481.588 1.016.658 1.593.074.617-.007 1.336-.25 2.177l-.013.043v.038c-.571 2.516-1.55 4.148-2.908 4.854-.673.35-1.467.547-2.358.587h-.002c-.16.007-.324.01-.492.01H9.673a.641.641 0 00-.633.539l-.06.355-1.04 6.573-.044.268a.107.107 0 01-.106.09h-.715z"/></svg>
          </span>
          <div>
            <p className="finance-kicker">Finanzen · Zahlungseingänge</p>
            <h1 className="finance-title">PayPal-Zahlungen</h1>
            <p className="finance-subtitle">Digitale Getränkezahlungen prüfen und gesammelt verbuchen</p>
          </div>
        </div>
        {/* Desktop: Verbuchen Button */}
        {selectedPayments.length > 0 && (
          <button
            onClick={() => handleMarkAsAccounted(selectedPayments)}
            disabled={Object.values(processing).some((p) => p)}
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
            {selectedPayments.length} verbuchen ({formatCurrency(totalAmount)})
          </button>
        )}
      </header>

      {/* Dashboard Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 mb-6">
        {/* KPI Card */}
        <div className="md:col-span-1 lg:col-span-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1c2432] p-4 sm:p-5">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Gesamtbetrag</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-2 tabular-nums">{formatCurrency(stats.summe)}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{stats.total} Zahlungen</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${stats.total > 0 ? (stats.verbucht / stats.total) * 100 : 0}%`,
                  background: stats.verbucht > 0 ? '#22c55e' : 'transparent',
                }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-gray-400 dark:text-gray-500">{stats.total > 0 ? Math.round((stats.verbucht / stats.total) * 100) : 0}% verbucht</span>
              {stats.offen > 0 && <span className="text-[10px] text-amber-600 dark:text-amber-400">{Math.round((stats.offen / stats.total) * 100)}% offen</span>}
            </div>
          </div>
          {/* Mobile/Tablet: inline verbucht/offen */}
          <div className="grid grid-cols-2 gap-3 mt-3 lg:hidden">
            <div className="text-center p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <p className="text-lg font-bold text-primary-700 dark:text-primary-300">{stats.verbucht}</p>
              <p className="text-[10px] text-primary-600 dark:text-primary-400">Verbucht</p>
            </div>
            <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{stats.offen}</p>
              <p className="text-[10px] text-amber-600 dark:text-amber-400">Offen</p>
            </div>
          </div>
        </div>

        {/* Bar Chart: Zahlungstrend */}
        <div className="md:col-span-1 lg:col-span-5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1c2432] p-4 sm:p-5">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Zahlungstrend pro Monat</p>
          {monthlyData.length > 0 ? (
            <>
              <div ref={chartScrollRef} className="flex gap-1.5 sm:gap-2 h-28 sm:h-32 overflow-x-auto">
                {monthlyData.map((m, i) => (
                  <div key={i} className="flex flex-col items-center group/bar relative" style={{ minWidth: '32px', flex: '1 0 auto' }}>
                    <div className="flex-1 w-full flex flex-col justify-end">
                      <div
                        className="w-full flex flex-col rounded-t overflow-hidden cursor-pointer"
                        style={{ height: `${Math.max(m.verbuchtPercent + m.offenPercent, 3)}%` }}
                      >
                        {m.offen > 0 && (
                          <div
                            className="w-full bg-amber-400 dark:bg-amber-500"
                            style={{ flex: m.offen }}
                          />
                        )}
                        {m.verbucht > 0 && (
                          <div
                            className="w-full bg-primary-400 dark:bg-primary-500"
                            style={{ flex: m.verbucht }}
                          />
                        )}
                      </div>
                    </div>
                    <span className="text-[8px] sm:text-[9px] text-gray-400 dark:text-gray-500 mt-1 flex-shrink-0">{m.label}</span>
                    {/* Hover Tooltip */}
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover/bar:block z-10 pointer-events-none">
                      <div className="bg-gray-900 dark:bg-gray-700 text-white text-[9px] rounded px-1.5 py-1 whitespace-nowrap shadow-lg">
                        <div>{formatCurrency(m.total)}</div>
                        {m.offen > 0 && m.verbucht > 0 && (
                          <div className="text-[8px] text-gray-300">
                            <span className="text-primary-300">{formatCurrency(m.verbucht)}</span>
                            {' · '}
                            <span className="text-amber-300">{formatCurrency(m.offen)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Legende */}
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-primary-400 dark:bg-primary-500" />
                  <span className="text-[9px] text-gray-500 dark:text-gray-400">Verbucht</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-amber-400 dark:bg-amber-500" />
                  <span className="text-[9px] text-gray-500 dark:text-gray-400">Offen</span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-28 sm:h-32 text-sm text-gray-400 dark:text-gray-500">
              Keine Daten
            </div>
          )}
        </div>

        {/* Donut Chart: Verbucht vs Offen (Desktop only) */}
        <div className="hidden lg:block lg:col-span-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1c2432] p-5">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Verbucht vs. Offen</p>
          <div className="flex items-center gap-6">
            {/* SVG Donut */}
            <div className="relative w-28 h-28 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-28 h-28">
                {/* Background ring */}
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={stats.offen > 0 ? '#fbbf24' : '#e5e7eb'}
                  strokeWidth="3.5"
                  strokeDasharray="100, 100"
                />
                {/* Verbucht ring */}
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="3.5"
                  strokeDasharray={`${verbuchtPercent}, 100`}
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold text-gray-900 dark:text-white">{stats.offen > 0 ? stats.offen : stats.verbucht}</span>
                <span className="text-[9px] text-gray-500 dark:text-gray-400">{stats.offen > 0 ? 'offen' : 'verbucht'}</span>
              </div>
            </div>
            {/* Legend */}
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-amber-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Offen</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white ml-5 tabular-nums">{formatCurrency(stats.summeOffen)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 ml-5">{stats.offen} Zahlungen</p>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-primary-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Verbucht</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white ml-5 tabular-nums">{formatCurrency(stats.summeVerbucht)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 ml-5">{stats.verbucht} Zahlungen</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center justify-between mb-5">
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
          />
          <span className="hidden sm:inline">Auch verbuchte anzeigen</span>
          <span className="sm:hidden">Verbuchte</span>
        </label>
        <select
          value={selectedYear || ''}
          onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Alle</option>
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Mobile: Verbuchen Button */}
      {selectedPayments.length > 0 && (
        <button
          onClick={() => handleMarkAsAccounted(selectedPayments)}
          disabled={Object.values(processing).some((p) => p)}
          className="sm:hidden w-full mb-4 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
          {selectedPayments.length} verbuchen ({formatCurrency(totalAmount)})
        </button>
      )}

      {/* Payment Groups */}
      {Object.keys(groupedPayments).length === 0 ? (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1c2432]">
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <CreditCardIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Keine PayPal-Zahlungen gefunden</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Es gibt keine Zahlungen für die gewählten Filter</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedPayments).map(([event, payments]) => {
            const openPayments = payments.filter(p => !p.accounted);
            const totalEventAmount = payments.reduce((sum, p) => sum + p.amount, 0);
            const isExpanded = expandedGroups[event];

            return (
              <div key={event} className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1c2432] overflow-hidden">
                {/* Group Header */}
                <div
                  className={`flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${isExpanded ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}
                  onClick={() => toggleGroup(event)}
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    {openPayments.length > 0 && (
                      <input
                        type="checkbox"
                        checked={openPayments.every((p) => selectedPayments.includes(p.id))}
                        onChange={() => toggleSelectAllInGroup(event)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{event}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {payments.length} Zahlungen · {formatCurrency(totalEventAmount)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {openPayments.length > 0 && (
                      <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                        {openPayments.length} offen
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Payment Rows */}
                {isExpanded && (
                  <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {payments.map((payment) => {
                      const avatar = getAvatarColor(payment.member_name);
                      return (
                        <div
                          key={payment.id}
                          className={`flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-gray-50 dark:hover:bg-gray-700/20 transition ${
                            payment.accounted ? 'bg-gray-50/50 dark:bg-gray-900/20' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            {!payment.accounted ? (
                              <input
                                type="checkbox"
                                checked={selectedPayments.includes(payment.id)}
                                onChange={() => toggleSelectPayment(payment.id)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded flex-shrink-0"
                              />
                            ) : (
                              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                <CheckCircleIcon className="h-4 w-4 text-success-500" />
                              </div>
                            )}
                            {/* Avatar */}
                            <div className={`hidden sm:flex w-8 h-8 rounded-lg ${avatar.bg} items-center justify-center flex-shrink-0`}>
                              <span className={`text-xs font-bold ${avatar.text}`}>{getInitials(payment.member_name)}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                                {payment.member_name}
                              </p>
                              <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(payment.payment_date)}
                              </p>
                              <p className="sm:hidden text-[10px] text-gray-400 dark:text-gray-500">
                                {formatDateCompact(payment.payment_date)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className={`text-xs sm:text-sm font-semibold tabular-nums ${
                              payment.accounted
                                ? 'text-success-600 dark:text-success-400'
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {formatCurrency(payment.amount)}
                            </p>
                            {payment.accounted === 1 && payment.cashbox_date && (
                              <p className="text-[10px] sm:text-xs text-success-600 dark:text-success-400">Verbucht</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, isOpen: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default PayPalPayments;
